/**
 * Telemetry Provider Component
 * 
 * Wraps the application with Application Insights context and error boundary.
 * Provides telemetry tracking capabilities to all child components.
 */

import React from 'react';
import { AppInsightsContext, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './appInsights';

/**
 * Error fallback component displayed when an unhandled error occurs
 */
const ErrorFallback = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '2rem',
    textAlign: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }}>
    <h1 style={{ color: '#dc3545', marginBottom: '1rem' }}>
      Oops! Something went wrong
    </h1>
    <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
      We're sorry, but an unexpected error occurred. Please try refreshing the page.
    </p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: '0.75rem 1.5rem',
        backgroundColor: '#0d6efd',
        color: 'white',
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        fontSize: '1rem'
      }}
    >
      Refresh Page
    </button>
  </div>
);

/**
 * TelemetryProvider component that wraps the application with:
 * - AppInsightsContext for telemetry tracking throughout the app
 * - AppInsightsErrorBoundary for automatic exception tracking
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to wrap
 */
const TelemetryProvider = ({ children }) => {
  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <AppInsightsErrorBoundary 
        onError={ErrorFallback} 
        appInsights={reactPlugin}
      >
        {children}
      </AppInsightsErrorBoundary>
    </AppInsightsContext.Provider>
  );
};

export default TelemetryProvider;

